import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, MenuItem, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Add, Close, Delete, Image as ImageIcon, Language, Phone, Reply } from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { prettyKey, wa } from './waTheme';

type CtaButton = { type: 'URL' | 'PHONE_NUMBER'; title: string; url: string; phone: string };
type ButtonMode = 'none' | 'cta' | 'quick';

/** Fields the sender fills automatically for each member. */
const AUTO_FIELDS: Record<string, string> = {
  userName: 'Member name',
  studioName: 'Studio name',
};

const CAMPAIGN = '__campaign__';
const PLACEHOLDER = /\{\{\s*(\d+)\s*\}\}/g;
const numbersIn = (s: string) => Array.from(String(s ?? '').matchAll(PLACEHOLDER), (m) => Number(m[1]));
const slug = (s: string) =>
  String(s ?? '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 64);

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called after a successful create with a message for the page toast. */
  onCreated: (message: string) => void;
  studioId?: string;
  /** Rows from the Templates list — the system messages a template can be used for. */
  systemTemplates: Array<{ templateKey: string; usedBy?: { description: string }[]; isCustom?: boolean }>;
  senderName?: string;
}

const CreateTemplateDialog: React.FC<Props> = ({ open, onClose, onCreated, studioId, systemTemplates, senderName }) => {
  const [useFor, setUseFor] = useState<string>(CAMPAIGN);
  const [name, setName] = useState('');
  const [category, setCategory] = useState<'MARKETING' | 'UTILITY'>('MARKETING');
  const [body, setBody] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [buttonMode, setButtonMode] = useState<ButtonMode>('none');
  const [buttons, setButtons] = useState<CtaButton[]>([]);
  const [quickReplies, setQuickReplies] = useState<string[]>(['']);
  const [vars, setVars] = useState<Record<number, { field: string; sample: string }>>({});
  const [submitNow, setSubmitNow] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setUseFor(CAMPAIGN); setName(''); setCategory('MARKETING'); setBody(''); setMediaUrl('');
    setButtonMode('none'); setButtons([]); setQuickReplies(['']); setVars({}); setSubmitNow(true); setError(null);
  }, [open]);

  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [error]);

  const systemOptions = systemTemplates.filter((t) => !t.isCustom);
  const isCampaign = useFor === CAMPAIGN;

  // Linking to a system message: suggest a dated name and the Utility category.
  const pickUseFor = (v: string) => {
    setUseFor(v);
    if (v === CAMPAIGN) {
      setCategory('MARKETING');
    } else {
      setCategory('UTILITY');
      const d = new Date();
      setName(`${v}_${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`);
    }
  };

  // Placeholders across the text and the end of link buttons, numbered once.
  const urlTexts = buttonMode === 'cta' ? buttons.filter((b) => b.type === 'URL').map((b) => b.url) : [];
  const placeholderNumbers = useMemo(() => {
    const all = [...numbersIn(body), ...urlTexts.flatMap(numbersIn)];
    return Array.from(new Set(all)).sort((a, b) => a - b);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body, JSON.stringify(urlTexts)]);

  const nextNumber = (placeholderNumbers[placeholderNumbers.length - 1] ?? 0) + 1;

  const insertVariable = (field: string) => {
    const n = nextNumber;
    const token = `{{${n}}}`;
    const el = bodyRef.current;
    const at = el ? el.selectionStart ?? body.length : body.length;
    const next = body.slice(0, at) + token + body.slice(at);
    setBody(next);
    setVars((v) => ({ ...v, [n]: { field, sample: v[n]?.sample ?? (field === 'userName' ? 'Priya' : field === 'studioName' ? senderName ?? 'SweatSocial' : '') } }));
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(at + token.length, at + token.length);
    });
  };

  const handleImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\/(jpeg|png)$/.test(file.type)) {
      setError('Use a JPG or PNG image. WhatsApp templates accept only these for images.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('The image is larger than 5 MB, which is WhatsApp’s limit.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const res: any = await apiService.uploadFiles([file], 'whatsapp-templates');
      const link = (res?.fileLinks ?? res?.data?.fileLinks ?? [])[0];
      if (!link) throw new Error('The upload did not return a link.');
      setMediaUrl(link);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Image upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const sampleFor = (n: number) => vars[n]?.sample?.trim() || `{{${n}}}`;
  const renderSample = (s: string) => s.replace(PLACEHOLDER, (_, n) => sampleFor(Number(n)));

  const create = async () => {
    setError(null);
    if (!slug(name)) return setError('Give the template a name using letters or numbers.');
    if (!body.trim()) return setError('Write the message text.');
    for (const n of placeholderNumbers) {
      if (!vars[n]?.field?.trim()) return setError(`Choose what {{${n}}} is filled with.`);
      if (!vars[n]?.sample?.trim()) return setError(`Add a sample value for {{${n}}}. WhatsApp needs it to review the template.`);
    }
    setSaving(true);
    try {
      const res: any = await apiService.createMessagingTemplate(
        {
          ...(isCampaign ? {} : { templateKey: useFor }),
          name,
          category,
          body,
          mediaUrl: mediaUrl || undefined,
          buttons:
            buttonMode === 'cta'
              ? buttons.map((b) =>
                  b.type === 'URL'
                    ? { type: 'URL', title: b.title, url: b.url }
                    : { type: 'PHONE_NUMBER', title: b.title, phone: b.phone },
                )
              : undefined,
          quickReplies: buttonMode === 'quick' ? quickReplies.filter((q) => q.trim()) : undefined,
          variables: placeholderNumbers.map((n) => ({ field: vars[n].field.trim(), sample: vars[n].sample.trim() })),
          submit: submitNow,
        },
        studioId,
      );
      const out = res?.data ?? res;
      const key = out?.registration?.templateKey ?? slug(name);
      onCreated(
        out?.submitted
          ? `${prettyKey(key)} created and sent to WhatsApp for approval.`
          : out?.submitError
            ? `${prettyKey(key)} created, but the approval request failed: ${out.submitError}. Use Submit on its row to retry.`
            : `${prettyKey(key)} created as a draft. Use Submit on its row when it’s ready.`,
      );
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Could not create the template.');
    } finally {
      setSaving(false);
    }
  };

  const addButton = (type: 'URL' | 'PHONE_NUMBER') =>
    setButtons((b) => [...b, { type, title: '', url: type === 'URL' ? 'https://' : '', phone: type === 'PHONE_NUMBER' ? '+91' : '' }]);
  const callCount = buttons.filter((b) => b.type === 'PHONE_NUMBER').length;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pr: 6 }}>
        Create WhatsApp template
        <IconButton onClick={onClose} disabled={saving} sx={{ position: 'absolute', right: 12, top: 12 }} aria-label="Close">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* ── Form ── */}
          <Stack spacing={2.2} sx={{ flex: 1.3, minWidth: 0 }}>
            <TextField
              select size="small" label="Use for" value={useFor} onChange={(e) => pickUseFor(e.target.value)}
              helperText={isCampaign
                ? 'A campaign template you send from a WhatsApp workflow.'
                : 'Replaces the template this system message sends once WhatsApp approves it.'}
            >
              <MenuItem value={CAMPAIGN}>New campaign template</MenuItem>
              {systemOptions.map((t) => (
                <MenuItem key={t.templateKey} value={t.templateKey}>
                  {prettyKey(t.templateKey)}
                  {t.usedBy?.[0]?.description ? ` — ${t.usedBy[0].description}` : ''}
                </MenuItem>
              ))}
            </TextField>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                size="small" label="Template name" value={name} onChange={(e) => setName(e.target.value)} sx={{ flex: 1 }}
                placeholder="SweatTour Season 4"
                helperText={slug(name) ? `WhatsApp name: ${slug(name)}` : 'Lowercase letters, numbers and underscores are used.'}
              />
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Category</Typography>
                <ToggleButtonGroup size="small" exclusive value={category} onChange={(_, v) => v && setCategory(v)}>
                  <ToggleButton value="MARKETING">Marketing</ToggleButton>
                  <ToggleButton value="UTILITY">Utility</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: '-8px !important' }}>
              Marketing: offers, events and announcements; only reaches members who opted in. Utility: updates about a booking or pack.
            </Typography>

            {/* Image */}
            <Box>
              <Typography fontWeight={700} fontSize={14} mb={0.8}>Image (optional)</Typography>
              {mediaUrl ? (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box component="img" src={mediaUrl} alt="Template header" sx={{ width: 96, height: 64, objectFit: 'cover', borderRadius: 1.5 }} />
                  <Button size="small" color="inherit" startIcon={<Delete />} onClick={() => setMediaUrl('')}>Remove</Button>
                </Stack>
              ) : (
                <Button component="label" size="small" variant="outlined" startIcon={uploading ? <CircularProgress size={14} /> : <ImageIcon />} disabled={uploading}>
                  {uploading ? 'Uploading…' : 'Upload JPG or PNG'}
                  <input id="wa-template-image" hidden type="file" accept="image/jpeg,image/png" onChange={handleImage} />
                </Button>
              )}
            </Box>

            {/* Message */}
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.8} flexWrap="wrap" gap={1}>
                <Typography fontWeight={700} fontSize={14}>Message</Typography>
                <Stack direction="row" spacing={0.8} flexWrap="wrap">
                  <Chip size="small" icon={<Add />} label="Member name" onClick={() => insertVariable('userName')} />
                  <Chip size="small" icon={<Add />} label="Studio name" onClick={() => insertVariable('studioName')} />
                  <Chip size="small" icon={<Add />} label="Other value" onClick={() => insertVariable('')} />
                </Stack>
              </Stack>
              <TextField
                id="wa-template-body"
                inputRef={bodyRef}
                multiline minRows={5} fullWidth size="small" value={body} onChange={(e) => setBody(e.target.value)}
                placeholder={'Hi {{1}}, the SweatTour Season 4 map is live! Pick your stops and book your spot.'}
                helperText={`${body.length} / 1024 · Use *bold* and _italic_. A message can't start or end with a variable.`}
                error={body.length > 1024}
              />
            </Box>

            {placeholderNumbers.length > 0 && (
              <Box>
                <Typography fontWeight={700} fontSize={14} mb={0.8}>Variables</Typography>
                <Stack spacing={1}>
                  {placeholderNumbers.map((n) => {
                    const field = vars[n]?.field ?? '';
                    const auto = !!AUTO_FIELDS[field];
                    return (
                      <Stack key={n} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                        <Chip label={`{{${n}}}`} size="small" sx={{ fontFamily: 'monospace', fontWeight: 700, alignSelf: 'flex-start', mt: { sm: 0.5 } }} />
                        <TextField
                          select size="small" label="Filled with" sx={{ minWidth: 170 }}
                          value={auto ? field : field ? '__custom' : ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setVars((s) => ({ ...s, [n]: { field: v === '__custom' ? '' : v, sample: s[n]?.sample ?? '' } }));
                          }}
                        >
                          <MenuItem value="userName">Member name (automatic)</MenuItem>
                          <MenuItem value="studioName">Studio name (automatic)</MenuItem>
                          <MenuItem value="__custom">Typed by the sender…</MenuItem>
                        </TextField>
                        {!auto && (
                          <TextField
                            size="small" label="Field name" placeholder="eventDate" sx={{ flex: 1 }}
                            value={field}
                            onChange={(e) => setVars((s) => ({ ...s, [n]: { field: e.target.value.replace(/[^A-Za-z0-9_]/g, ''), sample: s[n]?.sample ?? '' } }))}
                          />
                        )}
                        <TextField
                          size="small" label="Sample value" placeholder="Priya" sx={{ flex: 1 }}
                          value={vars[n]?.sample ?? ''}
                          onChange={(e) => setVars((s) => ({ ...s, [n]: { field: s[n]?.field ?? '', sample: e.target.value } }))}
                        />
                      </Stack>
                    );
                  })}
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block" mt={0.6}>
                  WhatsApp reviews the template with these samples. Values typed by the sender are asked for when the campaign is sent.
                </Typography>
              </Box>
            )}

            {/* Buttons */}
            <Box>
              <Typography fontWeight={700} fontSize={14} mb={0.8}>Buttons (optional)</Typography>
              <ToggleButtonGroup size="small" exclusive value={buttonMode} onChange={(_, v) => v && setButtonMode(v)}>
                <ToggleButton value="none">None</ToggleButton>
                <ToggleButton value="cta">Link / call</ToggleButton>
                <ToggleButton value="quick">Quick replies</ToggleButton>
              </ToggleButtonGroup>

              {buttonMode === 'cta' && (
                <Stack spacing={1.2} mt={1.5}>
                  {buttons.map((b, i) => (
                    <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                      <Chip size="small" icon={b.type === 'URL' ? <Language /> : <Phone />} label={b.type === 'URL' ? 'Website' : 'Call'} sx={{ alignSelf: 'flex-start', mt: { sm: 0.5 } }} />
                      <TextField
                        size="small" label="Button text" sx={{ width: { sm: 170 } }} value={b.title}
                        inputProps={{ maxLength: 25 }}
                        onChange={(e) => setButtons((s) => s.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))}
                      />
                      {b.type === 'URL' ? (
                        <TextField
                          size="small" label="Link" sx={{ flex: 1 }} value={b.url}
                          placeholder="https://sweatsocial.club/sweattour"
                          helperText="A variable is allowed only at the very end, e.g. …/events/{{2}}"
                          onChange={(e) => setButtons((s) => s.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))}
                        />
                      ) : (
                        <TextField
                          size="small" label="Phone number" sx={{ flex: 1 }} value={b.phone}
                          placeholder="+919876543210"
                          onChange={(e) => setButtons((s) => s.map((x, j) => (j === i ? { ...x, phone: e.target.value } : x)))}
                        />
                      )}
                      <IconButton aria-label="Remove button" onClick={() => setButtons((s) => s.filter((_, j) => j !== i))}><Delete fontSize="small" /></IconButton>
                    </Stack>
                  ))}
                  {buttons.length < 2 && (
                    <Stack direction="row" spacing={1}>
                      <Button size="small" startIcon={<Language />} onClick={() => addButton('URL')}>Add website button</Button>
                      {callCount === 0 && <Button size="small" startIcon={<Phone />} onClick={() => addButton('PHONE_NUMBER')}>Add call button</Button>}
                    </Stack>
                  )}
                  <Typography variant="caption" color="text.secondary">Up to 2 buttons, at most one call button. Labels up to 25 characters.</Typography>
                </Stack>
              )}

              {buttonMode === 'quick' && (
                <Stack spacing={1} mt={1.5}>
                  {quickReplies.map((q, i) => (
                    <Stack key={i} direction="row" spacing={1} alignItems="center">
                      <TextField
                        size="small" label={`Reply ${i + 1}`} value={q} sx={{ flex: 1 }} inputProps={{ maxLength: 20 }}
                        placeholder={i === 0 ? "I'm in" : 'Not this time'}
                        onChange={(e) => setQuickReplies((s) => s.map((x, j) => (j === i ? e.target.value : x)))}
                      />
                      {quickReplies.length > 1 && (
                        <IconButton aria-label="Remove reply" onClick={() => setQuickReplies((s) => s.filter((_, j) => j !== i))}><Delete fontSize="small" /></IconButton>
                      )}
                    </Stack>
                  ))}
                  {quickReplies.length < 3 && (
                    <Button size="small" startIcon={<Add />} sx={{ alignSelf: 'flex-start' }} onClick={() => setQuickReplies((s) => [...s, ''])}>Add reply</Button>
                  )}
                  <Typography variant="caption" color="text.secondary">Up to 3 replies, 20 characters each.</Typography>
                </Stack>
              )}
            </Box>

            <FormControlLabel
              control={<Switch checked={submitNow} onChange={(e) => setSubmitNow(e.target.checked)} />}
              label="Send to WhatsApp for approval now"
            />
            {error && <Alert ref={errorRef} severity="error">{error}</Alert>}
          </Stack>

          {/* ── Preview ── */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>Preview with sample values</Typography>
            <Box sx={{ bgcolor: '#efeae2', borderRadius: 3, p: 2, position: { md: 'sticky' }, top: 0 }}>
              <Box sx={{ bgcolor: '#fff', borderRadius: 2, overflow: 'hidden', maxWidth: 340, boxShadow: '0 1px 1px rgba(0,0,0,0.12)' }}>
                {mediaUrl && <Box component="img" src={mediaUrl} alt="" sx={{ width: '100%', display: 'block', maxHeight: 200, objectFit: 'cover' }} />}
                <Typography sx={{ p: 1.4, whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.45, wordBreak: 'break-word' }}>
                  {body.trim() ? renderSample(body) : 'Your message will appear here.'}
                </Typography>
                {buttonMode === 'cta' && buttons.map((b, i) => (
                  <Box key={i} sx={{ borderTop: '1px solid #e9edef', py: 1, textAlign: 'center', color: '#027eb5', fontSize: 14, fontWeight: 500, display: 'flex', justifyContent: 'center', gap: 0.8, alignItems: 'center' }}>
                    {b.type === 'URL' ? <Language sx={{ fontSize: 16 }} /> : <Phone sx={{ fontSize: 16 }} />}
                    {b.title || (b.type === 'URL' ? 'Visit website' : 'Call')}
                  </Box>
                ))}
              </Box>
              {buttonMode === 'quick' && (
                <Stack spacing={0.6} mt={0.6} sx={{ maxWidth: 340 }}>
                  {quickReplies.filter((q) => q.trim()).map((q, i) => (
                    <Box key={i} sx={{ bgcolor: '#fff', borderRadius: 2, py: 1, textAlign: 'center', color: '#027eb5', fontSize: 14, display: 'flex', justifyContent: 'center', gap: 0.8, alignItems: 'center' }}>
                      <Reply sx={{ fontSize: 16 }} /> {q}
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" mt={1.2}>
              Approval usually takes a few minutes and can take up to 24 hours. Approved templates can’t be edited; create a new one to change the wording.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained" onClick={create} disabled={saving || uploading}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={{ bgcolor: wa.teal, '&:hover': { bgcolor: wa.dark } }}
        >
          {submitNow ? 'Create and submit' : 'Create draft'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTemplateDialog;
